import { useAuth0 } from '@auth0/auth0-react'
import { GlobalIcon, LogoutIcon } from '@tractian/icons'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@tractian/ui'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { changeLanguage, supportedLngs, type SupportedLanguage } from '@/app/i18n'
import { readMatrix } from '@/app/matrix'
import { paths } from '@/routes/routes'

const LANGUAGE_SHORT_CODE: Record<SupportedLanguage, string> = {
  en: 'EN',
  'pt-BR': 'PT-BR'
}

const LANGUAGE_ITEM_KEY: Record<SupportedLanguage, string> = {
  en: 'shell.language.en',
  'pt-BR': 'shell.language.ptBR'
}

/**
 * Global app header for the Scope shell. Branded home affordance on the left
 * (TRACTIAN wordmark linking to the assessments list), a language selector, and
 * a direct logout action on the right. No product navigation — the app flow is
 * intentionally centred on the assessments list.
 */
export function MainHeader() {
  const { t, i18n } = useTranslation('common')
  const { logout } = useAuth0()

  const activeLanguage = (i18n.language.startsWith('pt') ? 'pt-BR' : 'en') as SupportedLanguage

  function handleLanguageChange(next: SupportedLanguage) {
    changeLanguage(i18n, next)
  }

  function handleLogout() {
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return (
    <header
      data-testid="scope-main-header"
      className="flex h-12 shrink-0 items-center gap-5 bg-shapes-platform-header px-4 text-white"
    >
      <Link
        to="/"
        aria-label={t('shell.homeLabel')}
        className="flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        <img
          src="/brand/tractian-wordmark-white.svg"
          alt="TRACTIAN"
          className="h-3.5 w-auto"
        />
      </Link>

      <div className="ml-auto flex items-center gap-2">
        {readMatrix() !== null && (
          <Link
            to={paths.matrix()}
            aria-label={t('shell.matrixIndicator')}
            title={t('shell.matrixIndicator')}
            data-testid="scope-main-matrix-link"
            className="flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <Badge
              size="small"
              shape="square"
              className="cursor-pointer bg-red-500 px-1 py-0 text-white"
              data-testid="scope-main-matrix-indicator"
            >
              ⚠ {t('shell.matrixTag')}
            </Badge>
          </Link>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="link"
              size="medium"
              prefixIcon={<GlobalIcon />}
              aria-label={t('shell.language.label')}
              data-testid="scope-main-language-trigger"
              className="!text-white hover:!text-white/80"
            >
              {LANGUAGE_SHORT_CODE[activeLanguage]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid="scope-main-language-menu">
            {supportedLngs.map(lng => (
              <DropdownMenuCheckboxItem
                key={lng}
                checked={lng === activeLanguage}
                onCheckedChange={() => handleLanguageChange(lng)}
                onSelect={e => e.preventDefault()}
                className="cursor-pointer gap-2"
                data-testid={`scope-main-language-option-${lng}`}
              >
                {t(LANGUAGE_ITEM_KEY[lng])}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          type="button"
          variant="link"
          size="medium"
          prefixIcon={<LogoutIcon />}
          onClick={handleLogout}
          aria-label={t('shell.logout')}
          data-testid="scope-main-logout"
          className="!text-white hover:!text-white/80"
        >
          {t('shell.logout')}
        </Button>
      </div>
    </header>
  )
}
